import { useMutation, UseMutationOptions } from 'react-query';
import { useTranslation } from '@frontend/i18n';
import { PhoneRecordsSchemaService } from '@frontend/schema';
import { useAlert } from '@frontend/design-system';

type DeleteCallRecordingVariables = {
  channelId?: string;
  locationId?: string;
};

type UseDeleteCallRecordingOptions<TContext = unknown> = Omit<
  UseMutationOptions<unknown, unknown, DeleteCallRecordingVariables, TContext>,
  'mutationFn'
> & {
  showSuccessAlert?: boolean;
  showErrorAlert?: boolean;
};

const deleteCallRecordings = async ({ channelId, locationId }: DeleteCallRecordingVariables) => {
  if (channelId && locationId) {
    return PhoneRecordsSchemaService.DeleteCallRecordings({
      channelIds: [channelId],
      locationIds: [locationId],
    });
  }
  return Promise.reject(new Error('Invalid media data'));
};

export const useDeleteCallRecording = <TContext = unknown>(options?: UseDeleteCallRecordingOptions<TContext>) => {
  const { t } = useTranslation('calls');
  const alert = useAlert();
  const { showSuccessAlert = true, showErrorAlert = true, ...mutationOptions } = options || {};

  return useMutation<unknown, unknown, DeleteCallRecordingVariables, TContext>(deleteCallRecordings, {
    ...mutationOptions,
    onError: (err, variables, context) => {
      if (showErrorAlert) {
        alert.error(t('Media deletion failed.'));
      }
      mutationOptions.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      if (showSuccessAlert) {
        alert.success(t('Call Recording successfully deleted.'));
      }
      mutationOptions.onSuccess?.(data, variables, context);
    },
  });
};
